import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Gift, CheckCircle, Mail, Home, Copy, Loader2 } from 'lucide-react';
import Footer from '../components/Footer';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GiftCardSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [activated, setActivated] = useState(false);
  
  const giftCardCode = searchParams.get('code');

  useEffect(() => {
    const activateCard = async () => {
      if (!giftCardCode) {
        setLoading(false);
        return;
      }

      try {
        await axios.post(`${API}/gift-cards/activate/${giftCardCode}`);
        setActivated(true);
      } catch (error) {
        setActivated(false);
      } finally {
        setLoading(false);
      }
    };

    activateCard();
  }, [giftCardCode]);

  const copyCode = () => {
    navigator.clipboard.writeText(giftCardCode);
    toast({
      title: '¡Copiado!',
      description: 'Código copiado al portapapeles',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Card className="p-8 text-center">
          <Loader2 className="w-12 h-12 mx-auto mb-4 text-black animate-spin" />
          <p className="text-gray-600">Activando tu tarjeta regalo...</p>
        </Card>
      </div>
    );
  }

  if (!activated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <Card className="max-w-lg w-full p-8 text-center" data-testid="gift-card-not-confirmed">
          <h1 className="text-2xl font-bold mb-3 text-gray-800">Pago pendiente de confirmar</h1>
          <p className="text-gray-600 mb-6">No hemos podido confirmar el pago de la tarjeta regalo. Si ya has pagado, espera unos minutos y recarga esta página, o escríbenos y lo revisamos.</p>
          <Button onClick={() => window.location.reload()} className="bg-black text-white" data-testid="gift-card-retry-btn">Volver a comprobar</Button>
          <Button onClick={() => navigate('/')} variant="ghost" className="ml-2" data-testid="gift-card-home-btn">Volver a la tienda</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          
          <h1 className="text-3xl font-bold mb-2 text-gray-800">
            ¡Tarjeta Regalo Activada!
          </h1>
          <p className="text-gray-600 mb-6">
            La tarjeta regalo se ha enviado por email al destinatario
          </p>

          {giftCardCode && (
            <div className="bg-gradient-to-r from-pink-100 to-purple-100 rounded-xl p-6 mb-6">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Gift className="w-5 h-5 text-black" />
                <span className="text-sm text-gray-600">Código de la tarjeta</span>
              </div>
              <div className="flex items-center justify-center gap-3">
                <span className="font-mono text-2xl font-bold text-black">
                  {giftCardCode}
                </span>
                <button
                  onClick={copyCode}
                  className="p-2 hover:bg-white/50 rounded-lg transition"
                  title="Copiar código"
                >
                  <Copy className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>
          )}

          <div className="bg-blue-50 rounded-lg p-4 mb-6 text-left">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <p className="font-semibold text-gray-800">Email enviado</p>
                <p className="text-sm text-gray-600">
                  El destinatario recibirá un email con el código y las instrucciones para usarlo.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              onClick={() => navigate('/')}
              className="w-full bg-black hover:opacity-90"
            >
              <Home className="w-4 h-4 mr-2" />
              Volver a la tienda
            </Button>
            <Button
              onClick={() => navigate('/gift-cards')}
              variant="outline"
              className="w-full"
            >
              <Gift className="w-4 h-4 mr-2" />
              Comprar otra tarjeta
            </Button>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default GiftCardSuccessPage;
